import { type FC, type SVGProps, useLayoutEffect, useRef } from "react";

import {
  type BBox,
  computeExpandedViewBox,
  parseViewBox,
} from "./auto-fit-view-box";

type Props = SVGProps<SVGSVGElement> & { node?: unknown };

/**
 * Renders agent-authored inline `<svg>` markup with a clipping repair: SVG
 * clips to its viewBox by default, and the generating model can only estimate
 * glyph widths (which vary per platform font), so labels and whisker caps
 * sometimes land outside the declared box and get cut mid-glyph. Measure the
 * real content bounding box and expand the viewBox to cover it.
 */
export const AutoFitSvg: FC<Props> = ({ node: _node, children, ...props }) => {
  const ref = useRef<SVGSVGElement>(null);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useLayoutEffect(() => {
    const el = ref.current;
    // A nested <svg> is clipped by its parent's viewport; fitting it alone
    // can't reveal the content, so only repair root elements.
    if (!el || el.ownerSVGElement) return;

    let canceled = false;
    const fit = () => {
      if (canceled) return;
      let bbox: BBox;
      try {
        bbox = el.getBBox();
      } catch {
        // Detached or display:none elements throw in some browsers.
        return;
      }
      // Not laid out yet (e.g. inside a collapsed panel).
      if (bbox.width === 0 && bbox.height === 0) return;

      const declared =
        parseViewBox(el.getAttribute("viewBox")) ??
        parseViewBox(
          `0 0 ${el.getAttribute("width")} ${el.getAttribute("height")}`,
        );
      if (!declared) return;

      const expanded = computeExpandedViewBox(declared, bbox);
      if (expanded) {
        el.setAttribute(
          "viewBox",
          `${expanded.minX} ${expanded.minY} ${expanded.width} ${expanded.height}`,
        );
      }
    };

    fit();
    // Text metrics change when web fonts finish loading; re-measure then.
    document.fonts?.ready.then(fit).catch(() => {});
    // Re-measure when the element gets laid out (a collapsed panel expanding,
    // print reveal) or resizes.
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(fit)
        : undefined;
    observer?.observe(el);
    return () => {
      canceled = true;
      observer?.disconnect();
    };
  }, [children]);

  return (
    <svg
      ref={ref}
      {...props}
      // Scale down inside narrow containers (chat panel, print) instead of
      // overflowing or being clipped by them.
      style={{ maxWidth: "100%", height: "auto", ...props.style }}
    >
      {children}
    </svg>
  );
};
